import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, Clock, MapPin, Send, CheckCircle2, AlertTriangle } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { BRAND } from "@/lib/brand";
import { SERVICE_KEYS } from "@/i18n";
import { TID } from "@/constants/testIds/dv";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactForm = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service) {
      setStatus({ state: "error", msg: t("contact.form.error") });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      await axios.post(`${API}/quotes`, form);
      setStatus({ state: "success", msg: t("contact.form.success") });
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      setStatus({ state: "error", msg: t("contact.form.error") });
    }
  };

  return (
    <section
      id="contact"
      data-testid={TID.contactSection}
      className="bg-white py-24 md:py-32 relative"
    >
      <div className="container-x">
        <div className="flex items-center gap-3 mb-4">
          <span className="h-px w-10 bg-[#E63946]" />
          <span className="label-eyebrow">{t("contact.eyebrow")}</span>
        </div>
        <h2 className="font-display uppercase text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight max-w-3xl">
          {t("contact.title")}
        </h2>
        <p className="mt-4 text-muted-foreground max-w-2xl">{t("contact.subtitle")}</p>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Contact info */}
          <div className="lg:col-span-2 space-y-4">
            <a
              href={BRAND.phoneHref}
              data-testid={TID.contactPhone}
              className="group flex items-start gap-4 border border-border p-5 rounded-sm hover:border-[#E63946] transition-colors"
            >
              <div className="h-10 w-10 grid place-items-center bg-[#E63946] text-white rounded-sm shrink-0">
                <Phone className="h-5 w-5" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
                  {t("contact.info.phone_label")}
                </div>
                <div className="font-display text-2xl font-bold mt-1 group-hover:text-[#E63946] transition-colors">
                  {BRAND.phone}
                </div>
              </div>
            </a>

            <a
              href={BRAND.emailHref}
              data-testid={TID.contactEmail}
              className="group flex items-start gap-4 border border-border p-5 rounded-sm hover:border-[#E63946] transition-colors"
            >
              <div className="h-10 w-10 grid place-items-center bg-black text-white rounded-sm shrink-0">
                <Mail className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
                  {t("contact.info.email_label")}
                </div>
                <div className="font-display text-lg md:text-xl font-bold mt-1 break-all group-hover:text-[#E63946] transition-colors">
                  {BRAND.email}
                </div>
              </div>
            </a>

            <div className="flex items-start gap-4 border border-border p-5 rounded-sm">
              <div className="h-10 w-10 grid place-items-center bg-black text-white rounded-sm shrink-0">
                <Clock className="h-5 w-5" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
                  {t("contact.info.hours_label")}
                </div>
                <div className="text-base font-semibold mt-1">{t("contact.info.hours_value")}</div>
              </div>
            </div>

            <div className="flex items-start gap-4 border border-border p-5 rounded-sm">
              <div className="h-10 w-10 grid place-items-center bg-black text-white rounded-sm shrink-0">
                <MapPin className="h-5 w-5" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
                  {t("contact.info.area_label")}
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {t("contact.info.area_value")}
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={submit}
            data-testid={TID.contactForm}
            className="lg:col-span-3 border border-border bg-white p-6 md:p-8 rounded-sm space-y-5"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-xs uppercase tracking-widest">
                  {t("contact.form.name")}
                </Label>
                <Input
                  id="name"
                  data-testid={TID.inputName}
                  value={form.name}
                  onChange={update("name")}
                  className="rounded-sm focus-visible:ring-[#E63946]"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs uppercase tracking-widest">
                  {t("contact.form.email")}
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid={TID.inputEmail}
                  value={form.email}
                  onChange={update("email")}
                  className="rounded-sm focus-visible:ring-[#E63946]"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-xs uppercase tracking-widest">
                  {t("contact.form.phone")}
                </Label>
                <Input
                  id="phone"
                  data-testid={TID.inputPhone}
                  value={form.phone}
                  onChange={update("phone")}
                  className="rounded-sm focus-visible:ring-[#E63946]"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest">
                  {t("contact.form.service")}
                </Label>
                <Select value={form.service} onValueChange={update("service")}>
                  <SelectTrigger
                    data-testid={TID.selectServiceTrigger}
                    className="rounded-sm focus:ring-[#E63946]"
                  >
                    <SelectValue placeholder={t("contact.form.select_service")} />
                  </SelectTrigger>
                  <SelectContent data-testid={TID.selectService}>
                    {SERVICE_KEYS.map((k) => (
                      <SelectItem key={k} value={k} data-testid={`service-option-${k}`}>
                        {t(`services.items.${k}.name`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message" className="text-xs uppercase tracking-widest">
                {t("contact.form.message")}
              </Label>
              <Textarea
                id="message"
                data-testid={TID.inputMessage}
                value={form.message}
                onChange={update("message")}
                placeholder={t("contact.form.message_ph")}
                className="rounded-sm min-h-[140px] focus-visible:ring-[#E63946]"
              />
            </div>

            {status.state === "success" && (
              <div
                data-testid={TID.successAlert}
                className="flex items-start gap-3 border border-green-600/30 bg-green-50 text-green-800 p-4 rounded-sm"
              >
                <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0" />
                <span className="text-sm">{status.msg}</span>
              </div>
            )}
            {status.state === "error" && (
              <div
                data-testid={TID.errorAlert}
                className="flex items-start gap-3 border border-red-600/30 bg-red-50 text-red-800 p-4 rounded-sm"
              >
                <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0" />
                <span className="text-sm">{status.msg}</span>
              </div>
            )}

            <button
              type="submit"
              data-testid={TID.submitBtn}
              disabled={status.state === "loading"}
              className="btn-primary w-full md:w-auto disabled:opacity-60"
            >
              {status.state === "loading" ? t("contact.form.submitting") : t("contact.form.submit")}
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
